import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import Slider from '@material-ui/core/Slider'
import AvatarEditor from 'react-avatar-editor'
import { Box, withStyles } from '@material-ui/core'
import { ifElse, propSatisfies, __, lt } from 'ramda'
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined'

import { SECONDARY } from 'app/constants/button'
import { compressImage } from 'libs/compressImage'
import { resizeAvatar_250 } from 'commons/resizeImage'
import { alertSizeOverLimit, PHOTO_SIZE_LIMIT_10 } from 'libs/file'
import Avatar from 'app/components/icons/avatar_empty.png'

import Avatars from '../Avatar/Avatars'
import SubmitDialog from '../Dialog/SubmitDialog'
import MessagespringLoading from '../Loader/MessagespringLoading'

const CustomSlider = withStyles((theme) => ({
  root: {
    height: 2,
    color: theme.colors['primary'],
    padding: '15px 0',
    width: 180,
  },
  thumb: {
    height: 16,
    width: 16,
    backgroundColor: theme.colors['primary'],
    boxShadow: 'none',
    marginTop: -7,
    marginLeft: -7,
    '&:focus, &:hover, &$active': {
      boxShadow: 'none',
    },
  },
  valueLabel: {
    left: 'calc(-50% + 12px)',
    top: -22,
    '& *': {
      background: 'transparent',
      color: '#000',
    },
  },
  track: {
    height: 4,
    borderRadius: 4,
  },
  rail: {
    height: 4,
    opacity: 0.5,
    backgroundColor: theme.colors['primary-350'],
  },
  mark: {
    backgroundColor: '#bfbfbf',
    height: 8,
    width: 1,
    marginTop: -3,
  },
  markActive: {
    opacity: 1,
    backgroundColor: theme.colors['primary'],
  },
}))(Slider)

const UploadImage = ({
  name,
  id = 0,
  src = '',
  onChange,
  isLoading,
  characters = '',
  error,
  title = 'Add Place Logo',
  noLimit = false,
  size = PHOTO_SIZE_LIMIT_10,
}) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const inputEl = useRef(null)
  const imageEditor = useRef(null)
  const [zoom, setZoom] = useState(1)
  const [imageFile, setImageFile] = useState(null)

  const handleClose = useCallback(() => {
    setImageFile(null)
    setZoom(1)
    if (inputEl.current) inputEl.current.value = ''
  }, [])

  const compressImageCallback = useCallback(() => {
    if (!imageEditor.current || !imageFile) return

    const canvas = imageEditor.current.getImageScaledToCanvas()
    canvas.toBlob(
      async (blob) => {
        if (!blob) return
        blob.name = imageFile.name
        const result = await compressImage(blob)
        result.name = imageFile.name
        onChange(result)
        handleClose()
      },
      imageFile.type,
      0.8,
    )
  }, [imageEditor, imageFile, onChange, handleClose])

  const storeImage = (file) => {
    if (!noLimit && file.size > size) {
      alertSizeOverLimit(dispatch, formatMessage, size)
      return
    }
    setImageFile(file)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    storeImage(file)
    e.target.value = ''
  }

  return (
    <>
      <div
        className={`relative inline-block w-[8rem] h-[8rem] rounded-lg overflow-hidden bg-gray-50 border ${error ? 'border-red-500' : 'border-primary-350'}`}
      >
        {isLoading ? (
          <div className="flex m-auto h-full">
            <div className="flex justify-center m-auto">
              <MessagespringLoading size={36} />
            </div>
          </div>
        ) : (
          <>
            {/* full-area transparent file input */}
            <input
              ref={inputEl}
              id={`upload-image-${name}`}
              type="file"
              accept="image/png, image/jpeg"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={handleFileSelect}
              onClick={(e) => {
                e.target.value = ''
              }}
              aria-label={`Upload Image ${name}`}
              data-testid={`upload-image-${name}`}
            />
            <Avatars
              id={id}
              name={name}
              characters={characters}
              src={src ? resizeAvatar_250(src) : Avatar}
              className="w-full h-full rounded-none"
            />
            <div className="bg-primary-350 rounded-4 absolute bottom-0 right-0 flex space-x-1 p-1 m-1 pointer-events-none">
              <CameraAltOutlinedIcon className="text-primary text-14" />
            </div>
          </>
        )}
      </div>

      <SubmitDialog
        id="dialog-crop-avatar"
        open={Boolean(imageFile)}
        title={title}
        submitText={formatMessage({ id: 'action.save' })}
        onSubmit={compressImageCallback}
        closeText={formatMessage({ id: 'action.cancel' })}
        onClose={handleClose}
        type={SECONDARY}
      >
        <Box className="flex flex-col items-center">
          <div style={{ width: '250px', height: '250px' }}>
            <AvatarEditor
              ref={(ref) => {
                imageEditor.current = ref
              }}
              image={imageFile}
              width={600}
              height={600}
              border={200}
              borderRadius={8}
              color={[200, 200, 200, 0.6]}
              scale={zoom}
              rotate={0}
              style={{ width: '250px', height: '250px' }}
            />
          </div>
          <Box className="mt-4">
            <CustomSlider
              value={zoom}
              min={0.5}
              max={3}
              step={0.01}
              onChange={(_, newVal) => setZoom(newVal)}
            />
          </Box>
        </Box>
      </SubmitDialog>
    </>
  )
}

UploadImage.propTypes = {
  id: PropTypes.number,
  error: PropTypes.bool,
  src: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isLoading: PropTypes.bool,
  characters: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

export default UploadImage
